import { lazyWithRetry } from "@circle-react/helpers/lazyRetry";
/* eslint-disable no-barrel-files/no-barrel-files -- Preserve the legacy editor's public exports without eagerly loading its implementation. */
import { Skeleton } from "@circleco/compass/components/Skeleton";
import { forwardRef, Suspense } from "react";

import type { TrixEditorRefObject } from "@/react/components/shared/TrixEditor/isTrixEditorRefObject";
import type { TrixEditorProps } from "@/react/components/shared/TrixEditor/TrixEditorImplementation";

export type { TrixEditorProps, TrixEditorRefObject };

const TRIX_ELEMENT_TIMEOUT_MS = 5000;

const waitForTrixElement = async () => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error("trix-editor registration timed out")),
      TRIX_ELEMENT_TIMEOUT_MS,
    );
  });

  try {
    await Promise.race([customElements.whenDefined("trix-editor"), timeout]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const LazyTrixEditor = lazyWithRetry(async () => {
  const [editor] = await Promise.all([
    import(
      /* webpackChunkName: "trix_editor" */ "@/react/components/shared/TrixEditor/TrixEditorImplementation"
    ),
    // Action Text does not import Trix. Import it here so whenDefined cannot hang
    // if the editor chunk stops pulling Trix in as a side effect.
    import(/* webpackChunkName: "trix" */ "trix"),
    import(/* webpackChunkName: "actiontext" */ "@rails/actiontext"),
  ]);

  // Trix 2.1.3 registers its custom elements on a timer after module evaluation.
  // Wait before mounting so the editor's effects can safely access element.editor.
  await waitForTrixElement();

  return { default: editor.TrixEditor };
});

/** @deprecated Please use TipTap editor instead for new components */
export const TrixEditor = forwardRef<TrixEditorRefObject, TrixEditorProps>(
  (props, ref) => (
    <Suspense
      fallback={
        <div className="flex w-full flex-col gap-2 p-3" aria-busy="true">
          <Skeleton shape="pill" className="h-4 w-full" />
          <Skeleton shape="pill" className="h-4 w-11/12" />
          <Skeleton shape="pill" className="h-4 w-5/6" />
        </div>
      }
    >
      <LazyTrixEditor {...props} ref={ref} />
    </Suspense>
  ),
);

TrixEditor.displayName = "TrixEditor";
/* eslint-enable no-barrel-files/no-barrel-files -- End legacy public-export compatibility exception. */
